import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import Button from "../../../../shared/ui/button/Button";
import Checkbox from "../../../../shared/ui/form/input/Checkbox";
import { Modal } from "../../../../shared/ui/modal";
import { useChangeMemberRoles, CenterMember, CenterRole, CenterType } from "../../../center";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    centerId: string;
    member: CenterMember;
    type: CenterType;
    /** The administrator looking: they cannot take their own administration away. */
    isYourself: boolean;
}

const ERRORS: Record<string, string> = {
    last_administrator: "center.team.rolesErrorLastAdmin",
    cannot_change_own_admin: "center.team.rolesErrorOwnAdmin",
    member_not_found: "center.departure.notFound",
};

/**
 * What somebody does in the centre, changed in place.
 *
 * Nobody is taken out and invited back to change a role: the seat, the plan
 * and everything they treat stay where they are. The one change that cannot
 * happen here in a single step is somebody who stops treating while the
 * centre's patients are still theirs — for that, the dialog points to the
 * handover, which ends in the same change.
 */
export default function MemberRolesModal({
    isOpen,
    onClose,
    centerId,
    member,
    type,
    isYourself,
}: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const change = useChangeMemberRoles(centerId);

    const [asAdmin, setAsAdmin] = useState(member.roles.includes("admin"));
    const [asTherapist, setAsTherapist] = useState(member.roles.includes("therapist"));
    const [error, setError] = useState<string | null>(null);
    const [needsHandover, setNeedsHandover] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setAsAdmin(member.roles.includes("admin"));
        setAsTherapist(member.roles.includes("therapist"));
        setError(null);
        setNeedsHandover(false);
    }, [isOpen, member]);

    const roles: CenterRole[] = [
        ...(asAdmin ? (["admin"] as CenterRole[]) : []),
        ...(asTherapist ? (["therapist"] as CenterRole[]) : []),
    ];
    const unchanged =
        roles.length === member.roles.length && roles.every((role) => member.roles.includes(role));
    const canSave = roles.length > 0 && !unchanged && !change.isPending;
    const name = `${member.first_name} ${member.last_name}`;

    const save = async () => {
        setError(null);
        setNeedsHandover(false);
        try {
            await change.mutateAsync({ membershipId: member.membership_id, roles });
            onClose();
        } catch (err: unknown) {
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail;
            if (detail === "member_has_patients") {
                setNeedsHandover(true);
                return;
            }
            setError(t((detail && ERRORS[detail]) || "center.team.rolesErrorGeneric"));
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            // The Modal is the dialog; naming it there, rather than a second
            // dialog role on the form, leaves one labelled dialog, not two.
            ariaLabel={t("center.team.rolesTitle", { name })}
            className="mx-4 w-full max-w-md rounded-2xl bg-surface-raised p-6"
        >
            <form
                className="flex flex-col gap-4"
                onSubmit={(e) => {
                    e.preventDefault();
                    void save();
                }}
            >
                <div>
                    <h3 className="text-lg font-semibold text-content-primary">
                        {t("center.team.rolesTitle", { name })}
                    </h3>
                    <p className="mt-1 text-sm text-content-tertiary">{t("center.team.rolesHint")}</p>
                </div>

                <fieldset className="flex flex-col gap-2">
                    <legend className="sr-only">{t("center.team.roles")}</legend>
                    <Checkbox
                        id="member-role-therapist"
                        checked={asTherapist}
                        onChange={setAsTherapist}
                        label={
                            type === "training"
                                ? t("center.invitations.roleTrainer")
                                : t("center.invitations.roleTherapist")
                        }
                    />
                    <Checkbox
                        id="member-role-admin"
                        checked={asAdmin}
                        onChange={setAsAdmin}
                        disabled={isYourself && member.roles.includes("admin")}
                        label={t("center.invitations.roleAdmin")}
                    />
                    {isYourself && member.roles.includes("admin") && (
                        <p className="text-xs text-content-tertiary">
                            {t("center.team.rolesErrorOwnAdmin")}
                        </p>
                    )}
                    {roles.length === 0 && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                            {t("center.invitations.rolesRequired")}
                        </p>
                    )}
                </fieldset>

                {needsHandover && (
                    <div
                        role="alert"
                        className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
                    >
                        <p className="mb-2">{t("center.team.rolesNeedsHandover", { name })}</p>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                onClose();
                                navigate(`/center/team/${member.membership_id}/stop-practising`);
                            }}
                        >
                            {t("center.team.rolesGoToHandover")}
                        </Button>
                    </div>
                )}

                {error && (
                    <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                        {error}
                    </p>
                )}

                <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose}>
                        {t("common.cancel")}
                    </Button>
                    <Button type="submit" disabled={!canSave} loading={change.isPending}>
                        {t("common.save")}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
